<template>
  <div
    class="fts-design-element"
    :style="{ overflow: item.input != 'description' ? 'hidden' : 'initial' }"
  >
    <div class="design-element-wrapper">
      <!-- custom:description -->
      <div
        class="design-element-custom-description"
        @blur="textSelection = null"
        v-if="item.input == 'description'"
      >
        <div class="editor-toolbar" v-if="editorBar">
          <template v-for="x in editorTools">
            <div
              class="editor-tool"
              v-if="!x.beta || (x.beta && beta)"
              :key="x.name"
            >
              <swatches
                @input="changeColor('forecolor', $event)"
                v-model="foreColor"
                row-length="7"
                :swatches="getForeColors"
                shapes="circles"
                popover-x="left"
                :swatch-size="25"
                v-if="x.name === 'forecolor'"
              >
                <button type="button" slot="trigger">
                  <div
                    style="display: inline-flex; border-bottom: 2px solid #000"
                  >
                    A
                  </div>
                </button>
              </swatches>

              <swatches
                @input="changeColor('backcolor', $event)"
                v-model="backColor"
                row-length="7"
                :swatches="getBackColors"
                shapes="circles"
                popover-x="left"
                :swatch-size="25"
                v-else-if="x.name === 'backcolor'"
              >
                <button type="button" slot="trigger">
                  <div>A</div>
                </button>
              </swatches>

              <button
                type="button"
                @click="openElementContentModal"
                v-else-if="x.name === 'link'"
              >
                <font-awesome-icon :icon="x.icon" v-if="x.icon" />
              </button>

              <button type="button" @click="contentEdit(x.name)" v-else>
                <font-awesome-icon :icon="x.icon" v-if="x.icon" />
                <template v-else>{{ x.text }}</template>
              </button>

              <fts-release-badge class="editor-tool-rb" v-if="x.beta" />
            </div>
          </template>
        </div>
        <div
          v-html="item.content"
          contenteditable
          class="editor-content"
          @blur="onEdit(item, $event)"
          @paste="onPaste"
          @mouseup="getTextSelection"
          ref="editor"
        >
          Enter your text here.
        </div>
      </div>
      <!-- custom:image -->
      <div
        class="design-element-custom-image"
        v-else-if="item.input == 'image'"
      >
        <fts-file-upload
          :vectorId="vectorId"
          :item="item.item"
          :formData="item"
        />
      </div>
      <!-- empty space -->
      <div
        class="design-element-empty-space"
        v-else-if="item.input == 'empty'"
      >
        <div>I help to reserve this space 🙈</div>
      </div>
      <!-- custom:button -->
      <div
        class="design-element-custom-button"
        v-else-if="item.input == 'button'"
      >
        <fts-btn
          outline
          :color="'primary'"
          text-transform-init
        >
          {{item.label ? item.label : "(unnamed)"}}
        </fts-btn>
      </div>
      <!-- section -->
      <template v-else-if="item.input === 'section'">
        <div class="design-element-section-header">
          <div class="section-header">
            <div
              class="section-header-title"
              @click="sectionSelection = !sectionSelection"
            >
              {{ item.type }}
            </div>
            <fts-toggle-button
              class="section-header-action"
              :formData="item"
              :item="{ fieldName: 'type' }"
              :options="['section', 'collapse', 'tab', 'column']"
              @select="sectionSelection = false"
              v-if="sectionSelection"
            />
          </div>
        </div>
        <div class="design-element-section-content">
          <template v-if="item.type !== 'tab'">
            <div class="section-content" v-if="item.type !== 'column'">
              <fts-contenteditable
                class="section-content-title"
                v-model="item.header"
              />
              <fts-toggle-switch
                class="section-content-action"
                :formData="item"
                :item="{ fieldName: 'expanded' }"
                :size="18"
                :labelDir="'left'"
                :labelText="'Expanded by Default'"
                v-if="item.type === 'collapse'"
              />
            </div>
            <fts-form-layouter
              :activityId="activityId"
              :activityDesign="form"
              :form="item.design"
              :itemGroup="'row'"
              @added="elementAdded"
              :formData="formData"
            ></fts-form-layouter>
          </template>
          <template v-else>
            <template v-for="tab in getTabs(item.tabs)">
              <div class="section-content-tab" :key="tab.id">
                <div class="section-content">
                  <fts-contenteditable
                    class="section-content-title"
                    v-model="tab.header"
                  />
                  <div class="design-element-actions">
                    <div
                      class="design-element-action action-remove"
                      @click="removeTab(tab)"
                    >
                      <font-awesome-icon
                        :icon="['fas', 'times']"
                        fixedWidth
                      />Remove
                    </div>
                  </div>
                </div>
                <fts-form-layouter
                  :form="tab.design"
                  :activityId="activityId"
                  :activityDesign="form"
                  :itemGroup="'row'"
                  @added="elementAdded"
                  :formData="formData"
                ></fts-form-layouter>
              </div>
              <div
                class="section-content-divider"
                :key="`tab + ${tab.id}`"
              ></div>
            </template>
            <button
              type="button"
              class="
                fts-btn fts-btn-text fts-btn-primary fts-btn-icon-mr
                section-content-new
              "
              @click="addTab()"
            >
              <font-awesome-icon :icon="['fas', 'plus']" fixedWidth />Add Tab
            </button>
          </template>
        </div>
      </template>
      <!-- design element default -->
      <div
        class="design-element__default"
        v-else-if="item.input != 'description'"
      >
        <i
          class="design-element-bg-icon"
          :class="getIcon(item.type)"
          v-if="getIcon(item.type)"
        />
        <div class="design-element-information">
          <i
            class="design-element-information-icon"
            :class="[
              getIcon(item.type),
              { required: item.required || item.requiredFormula },
            ]"
          />
          <div class="design-element-information-name">
            {{item.displayName}}
          </div>
        </div>
        <div
          class="fts-badge badge-default badge-xs badge-pill"
          v-if="item.options && item.options.uppercase"
        >
          uppercase
        </div>
        <div
          class="fts-badge badge-default badge-xs badge-pill"
          v-if="item.options && item.options.phoneRegionCode"
        >
          {{ item.options.phoneRegionCode }}
        </div>
        <div
          class="fts-badge badge-default badge-xs badge-pill"
          v-if="item.options && item.options.prefix"
        >
          {{ item.options.prefix }}
        </div>
        <div
          class="fts-badge badge-default badge-xs badge-pill"
          v-if="item.options && item.options.numeralDecimalScale"
        >
          decimal:
          <b>{{ item.options.numeralDecimalScale }}</b>
        </div>
        <div
          class="fts-badge badge-default badge-xs badge-pill"
          v-if="item.type === 8 || item.type === 11 || item.type === 13"
        >
          multiple
        </div>
      </div>
      <!-- design element table -->
      <div
        class="design-element__table design-element-table"
        v-if="
          item.input == 'table' ||
          (item.type == 14 && Array.isArray(item.fields))
        "
      >
        <draggable
          class="design-element-table-columns"
          :group="item.fieldName"
          :list="item.fields"
          v-bind="dragOptions"
          handle=".handle"
        >
          <div
            class="design-element-table-column"
            v-for="column in item.fields"
            :key="column.id"
            :class="{ disabled: column && column.hide }"
          >
            <font-awesome-icon
              class="design-element-table-column-handle handle"
              :icon="['fas', 'grip-vertical']"
              fixedWidth
            />
            <div class="design-element-table-column-wrapper">
              <!-- design element default -->
              <div class="design-element__default">
                <i
                  class="design-element-bg-icon"
                  :class="getIcon(column.type)"
                  v-if="getIcon(column.type)"
                />
                <div class="design-element-information">
                  <i
                    class="design-element-information-icon"
                    :class="[
                      getIcon(column.type),
                      {
                        required: column.required || column.requiredFormula,
                        disabled: column && column.hide,
                      },
                    ]"
                  />
                  <div
                    class="design-element-information-name"
                    :class="{ disabled: column && column.hide }"
                  >
                    {{ column.displayName }}
                  </div>
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.options && column.options.uppercase"
                >
                  uppercase
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.options && column.options.phoneRegionCode"
                >
                  {{ item.options.phoneRegionCode }}
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.options && column.options.prefix"
                >
                  {{ item.options.prefix }}
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.options && column.options.numeralDecimalScale"
                >
                  decimal:
                  <b>{{ column.options.numeralDecimalScale }}</b>
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="
                    column.type === 8 ||
                    column.type === 11 ||
                    column.type === 13
                  "
                >
                  multiple
                </div>
              </div>
              <!-- Controls -->
              <div class="design-element__control" v-if="!document">
                <div class="design-element-controls">
                  <template v-for="action in getActions(column)">
                    <i
                      class="design-element-control fts-icon-asterisk"
                      :class="{ active: column.required }"
                      :key="action"
                      @click="updateItem(column, 'required')"
                      v-if="action === 'required'"
                    />
                    <i
                      class="design-element-control fts-icon-pencil-slash"
                      :class="{ active: column.readOnly }"
                      :key="action"
                      @click="updateItem(column, 'readOnly')"
                      v-else-if="action === 'readOnly'"
                    />
                    <i
                      class="design-element-control fts-icon-user-check"
                      :class="{ active: column.assignee }"
                      :key="action"
                      @click="updateItem(column, 'assignee')"
                      v-else-if="action === 'assignee'"
                    />
                    <i
                      class="design-element-control fts-icon-calendar-check"
                      :class="{ active: column.due }"
                      :key="action"
                      @click="updateItem(column, 'due')"
                      v-else-if="action === 'due'"
                    />
                    <i
                      class="design-element-control fts-icon-add-remove"
                      :class="{ active: !column.disabledAddRemoveItems }"
                      :key="action"
                      @click="updateItem(column, 'disabledAddRemoveItems')"
                      v-else-if="action === 'addable'"
                    />
                    <i
                      class="design-element-control fts-icon-hidden"
                      :class="{ active: column.visibilityScript }"
                      :key="action"
                      @click="openScriptModal(column, 'visibilityScript')"
                      v-else-if="action === 'visible'"
                    />
                    <!-- to-do visible control -->
                    <i
                      class="design-element-control fts-icon-formula"
                      :class="{ active: formulaScript(column) }"
                      :key="action"
                      @click="openFormulaModal(column)"
                      v-else-if="action === 'formula'"
                    />
                  </template>
                </div>

                <template v-for="action in getActions(column)">
                  <i
                    class="design-element-control-tag active fts-icon-asterisk"
                    :key="action"
                    v-if="action === 'required' && column.required"
                  />
                  <i
                    class="design-element-control-tag active fts-icon-pencil-slash"
                    :key="action"
                    v-else-if="action === 'readOnly' && column.readOnly"
                  />
                  <i
                    class="design-element-control-tag active fts-icon-user-check"
                    :key="action"
                    v-else-if="action === 'assignee' && column.assignee"
                  />
                  <i
                    class="
                      design-element-control-tag
                      active
                      fts-icon-calendar-check
                    "
                    :key="action"
                    v-else-if="action === 'due' && column.due"
                  />
                  <i
                    class="design-element-control-tag active fts-icon-add-remove"
                    :key="action"
                    v-else-if="
                      action === 'addable' && !column.disabledAddRemoveItems
                    "
                  />
                  <i
                    class="design-element-control-tag active fts-icon-hidden"
                    :key="action"
                    v-else-if="action === 'visible' && column.visibilityScript"
                  />
                  <!-- to-do visible display -->
                  <i
                    class="design-element-control-tag active fts-icon-formula"
                    :key="action"
                    v-else-if="action === 'formula' && formulaScript(column)"
                  />
                </template>

                <div class="design-element-formula-tag" v-if="column.formula">
                  <i class="formula-tag-icon fts-icon-formula" />
                  <fts-formula-display
                    :variables="variables"
                    v-model="column.formula"
                  />
                </div>
                <div class="design-element-formula-tag" v-if="column.requiredFormula">
                  <i class="formula-tag-icon fts-icon-asterisk" />
                  <fts-formula-display
                    :variables="variables"
                    v-model="column.requiredFormula"
                  />
                </div>
                <div class="design-element-formula-tag" v-if="column.readOnlyFormula">
                  <i class="formula-tag-icon fts-icon-pencil-slash" />
                  <fts-formula-display
                    :variables="variables"
                    v-model="column.readOnlyFormula"
                  />
                </div>
                <div class="design-element-formula-tag" v-if="column.visibleFormula">
                  <i class="formula-tag-icon fts-icon-hidden" />
                  <fts-formula-display
                    :variables="variables"
                    v-model="column.visibleFormula"
                  />
                </div>
                <div class="design-element-formula-tag" v-if="column.queryScript">
                  <i class="formula-tag-icon fts-icon-code" />
                  <div>Query Script</div>
                </div>
              </div>
              <!-- Actions -->
              <div class="design-element-actions">
                <div
                  class="design-element-action action-control"
                  v-if="!column.hide && !document"
                  @click="configure(column)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'cog']"
                    fixedWidth
                  />Configure
                </div>
                <div
                  class="design-element-action action-remove"
                  v-if="!column.hide"
                  @click="hideColumn(column)"
                >
                  <font-awesome-icon :icon="['fas', 'times']" fixedWidth />Hide
                </div>
                <div
                  class="design-element-action action-add"
                  v-else
                  @click="showColumn(column)"
                >
                  <font-awesome-icon :icon="['fas', 'check']" fixedWidth />Show
                </div>
              </div>
            </div>
          </div>
        </draggable>
        <!-- preview -->
        <div class="design-element-table-preview-label">Preview</div>
        <draggable
          class="design-element-table-preview-columns fts-scrollbar"
          :list="item.fields"
          v-bind="dragOptions"
          handle=".preview-column-handle"
          draggable=".preview-column"
        >
          <div
            class="preview-column"
            v-for="column in item.fields.filter((x) => !x.hide)"
            :key="column.id"
            :class="{ disabled: column && column.hide }"
          >
            <font-awesome-icon
              class="preview-column-handle"
              :icon="['fas', 'grip-vertical']"
              fixed-width
            />
            <div class="preview-column-information">
              <i
                class="preview-column-information-icon"
                :class="[
                  getIcon(column.type),
                  {
                    required: column.required || column.requiredFormula,
                  },
                ]"
              />
              <div class="preview-column-information-name">{{column.displayName}}</div>
            </div>
          </div>
          <!-- preview:add -->
          <div slot="footer" class="design-element-table-preview-action">
            <fts-btn text icon>
              <font-awesome-icon :icon="['fas', 'plus']" />
            </fts-btn>
          </div>
        </draggable>
      </div>
      <!-- Form Element List (not implemented) -->
      <div
        class="design-element-table"
        v-if="
          item.input == 'list' ||
          (item.type == 15 && Array.isArray(item.fields))
        "
      >
        <draggable
          class="design-element-table-columns"
          :group="{ name: item.fieldName, pull: 'clone', put: false }"
          :list="item.fields"
          v-bind="dragOptions"
          handle=".handle"
        >
          <div
            class="design-element-table-column"
            v-for="column in item.fields"
            :key="column.id"
            :class="{ disabled: column && column.hide }"
          >
            <font-awesome-icon
              class="design-element-table-column-handle handle"
              :icon="['fas', 'grip-vertical']"
              fixedWidth
            />
            <div class="design-element-table-column-wrapper">
              <!-- design element default -->
              <div class="design-element__default">
                <div class="design-element-information">
                  <i
                    class="design-element-information-icon"
                    :class="[
                      getIcon(column.type),
                      {
                        required: column.required || column.requiredFormula,
                        disabled: column && column.hide,
                      },
                    ]"
                  />
                  <div
                    class="design-element-information-name"
                    :class="{ disabled: column && column.hide }"
                  >
                    {{ column.displayName }}
                  </div>
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.options && column.options.uppercase"
                >
                  uppercase
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.options && column.options.phoneRegionCode"
                >
                  {{ item.options.phoneRegionCode }}
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.options && column.options.prefix"
                >
                  {{ item.options.prefix }}
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.options && column.options.numeralDecimalScale"
                >
                  decimal:
                  <b>{{ column.options.numeralDecimalScale }}</b>
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="
                    column.type === 8 ||
                    column.type === 11 ||
                    column.type === 13
                  "
                >
                  multiple
                </div>
                <div class="fts-badge badge-default badge-xs badge-pill" v-if="column.due">
                  due
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.assignee"
                >
                  assignee
                </div>
                <div
                  class="fts-badge badge-default badge-xs badge-pill"
                  v-if="column.disabledAddRemoveItems"
                >
                  disabled add/remove
                </div>
              </div>
            </div>
          </div>
        </draggable>
        <div class="design-element-table-divider"></div>
        <fts-form-layouter
          class="design-element-list"
          :activityDesign="form"
          :activityId="activityId"
          :form="item.design"
          :itemGroup="item.fieldName"
          :formData="{}"
        />
      </div>
      <!-- design element control -->
      <div class="design-element__control" v-if="!document">
        <div class="design-element-controls">
          <template v-for="action in getActions(item)">
            <i
              class="design-element-control fts-icon-asterisk"
              :class="{ active: item.required }"
              :key="action"
              @click="updateItem(item, 'required')"
              v-if="action === 'required'"
            />
            <i
              class="design-element-control fts-icon-pencil-slash"
              :class="{ active: item.readOnly }"
              :key="action"
              @click="updateItem(item, 'readOnly')"
              v-else-if="action === 'readOnly'"
            />
            <i
              class="design-element-control fts-icon-user-check"
              :class="{ active: item.assignee }"
              :key="action"
              @click="updateItem(item, 'assignee')"
              v-else-if="action === 'assignee'"
            />
            <i
              class="design-element-control fts-icon-calendar-check"
              :class="{ active: item.due }"
              :key="action"
              @click="updateItem(item, 'due')"
              v-else-if="action === 'due'"
            />
            <i
              class="design-element-control fts-icon-add-remove"
              :class="{ active: !item.disabledAddRemoveItems }"
              :key="action"
              @click="updateItem(item, 'disabledAddRemoveItems')"
              v-else-if="action === 'addable'"
            />
            <i
              class="design-element-control fts-icon-hidden"
              :class="{ active: item.visibilityScript }"
              :key="action"
              @click="openScriptModal(item, 'visibilityScript')"
              v-else-if="action === 'visible'"
            />
            <!-- to-do visible control -->
            <i
              class="design-element-control fts-icon-formula"
              :class="{ active: formulaScript(item) }"
              :key="action"
              @click="openFormulaModal(item)"
              v-else-if="action === 'formula'"
            />
          </template>
        </div>

        <template v-for="action in getActions(item)">
          <i
            class="design-element-control-tag active fts-icon-asterisk"
            :key="action"
            v-if="action === 'required' && item.required"
          />
          <i
            class="design-element-control-tag active fts-icon-pencil-slash"
            :key="action"
            v-else-if="action === 'readOnly' && item.readOnly"
          />
          <i
            class="design-element-control-tag active fts-icon-user-check"
            :key="action"
            v-else-if="action === 'assignee' && item.assignee"
          />
          <i
            class="design-element-control-tag active fts-icon-calendar-check"
            :key="action"
            v-else-if="action === 'due' && item.due"
          />
          <i
            class="design-element-control-tag active fts-icon-add-remove"
            :key="action"
            v-else-if="action === 'addable' && !item.disabledAddRemoveItems"
          />
          <i
            class="design-element-control-tag active fts-icon-hidden"
            :key="action"
            v-else-if="action === 'visible' && item.visibilityScript"
          />
          <!-- to-do visible display -->
          <i
            class="design-element-control-tag active fts-icon-formula"
            :key="action"
            v-else-if="action === 'formula' && formulaScript(item)"
          />
        </template>

        <div class="design-element-formula-tag" v-if="item.formula">
          <i class="formula-tag-icon fts-icon-formula" />
          <fts-formula-display :variables="variables" v-model="item.formula" />
        </div>
        <div class="design-element-formula-tag" v-if="item.requiredFormula">
          <i class="formula-tag-icon fts-icon-asterisk" />
          <fts-formula-display
            :variables="variables"
            v-model="item.requiredFormula"
          />
        </div>
        <div class="design-element-formula-tag" v-if="item.readOnlyFormula">
          <i class="formula-tag-icon fts-icon-pencil-slash" />
          <fts-formula-display
            :variables="variables"
            v-model="item.readOnlyFormula"
          />
        </div>
        <div class="design-element-formula-tag" v-if="item.visibleFormula">
          <i class="formula-tag-icon fts-icon-hidden" />
          <fts-formula-display
            :variables="variables"
            v-model="item.visibleFormula"
          />
        </div>
        <div class="design-element-formula-tag" v-if="item.queryScript">
          <i class="formula-tag-icon fts-icon-code" />
          <div>Query Script</div>
        </div>
      </div>
      <!-- Actions -->
      <div class="design-element-actions">
        <div
          class="design-element-action action-control"
          @click="configure(item)"
          v-if="
            item.input !== 'empty' &&
            item.input !== 'section' &&
            item.input !== 'description' &&
            item.input !== 'image' &&
            !document
          "
        >
          <font-awesome-icon :icon="['fas', 'cog']" fixedWidth />Configure
        </div>
        <div class="design-element-action action-remove" @click="remove()">
          <font-awesome-icon :icon="['fas', 'times']" fixedWidth />Remove
        </div>
      </div>
    </div>
    <!-- Formula Modal -->
    <fts-modal
      class="formula-designer-modal designer-modal"
      :backdrop="false"
      :footer="false"
      headerClass="designer-header"
      contentClass="designer-body"
      @close="closeFormulaModal"
      v-if="formulaModal"
    >
      <!-- header -->
      <template #header>
        <div class="designer-header-wrapper">
          <div class="designer-header-title">
            Formula
            <span v-if="item.displayName">for {{ item.displayName }}</span>
          </div>
          <div class="designer-header-desc">Formula designer</div>
        </div>
        <div class="designer-header-actions">
          <fts-btn
            text
            icon
            class="designer-header-action"
            @click="closeFormulaModal"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
          </fts-btn>
        </div>
      </template>
      <!-- body -->
      <template #body>
        <!-- formula:value -->
        <div
          class="formula-wrapper"
          v-if="
            item.input !== 'description' &&
            item.input !== 'image' &&
            item.input !== 'section' &&
            item.input !== 'button' &&
            item.input !== 'empty'
          "
        >
          <div class="formula-label">
            <i class="formula-icon fts-icon-formula" />
            <div class="formula-name">Formula</div>
          </div>
          <fts-formula
            :variables="variables"
            :form="form"
            v-model="formulaModalData.formula"
          />
        </div>
        <!-- formula:required -->
        <div
          class="formula-wrapper"
          v-if="getActions(item).indexOf('required') > -1"
        >
          <div class="formula-label">
            <i class="formula-icon fts-icon-asterisk" />
            <div class="formula-name">Required</div>
            <input type="checkbox" v-model="requiredCheck" />
          </div>
          <fts-formula
            :variables="variables"
            :form="form"
            v-model="formulaModalData.requiredFormula"
          />
        </div>
        <!-- formula:read only -->
        <div
          class="formula-wrapper"
          v-if="getActions(item).indexOf('readOnly') > -1"
        >
          <div class="formula-label">
            <i class="formula-icon fts-icon-pencil-slash" />
            <div class="formula-name">Read Only</div>
            <input type="checkbox" v-model="readOnlyCheck" />
          </div>
          <fts-formula
            :variables="variables"
            :form="form"
            v-model="formulaModalData.readOnlyFormula"
          />
        </div>
        <!-- formula:visible -->
        <div
          class="formula-wrapper"
          v-if="getActions(item).indexOf('visible') > -1"
        >
          <div class="formula-label">
            <i class="formula-icon fts-icon-hidden" />
            <div class="formula-name">Visible</div>
          </div>
          <fts-formula
            :variables="variables"
            :form="form"
            v-model="formulaModalData.visibleFormula"
          />
        </div>
      </template>
    </fts-modal>
    <!-- Script Modal -->
    <fts-modal
      class="designer-modal"
      appendToBody
      :backdrop="false"
      :footer="false"
      headerClass="designer-header"
      contentClass="designer-body"
      @close="closeScriptModal"
      v-if="showScriptModal"
    >
      <template #header>
        <div class="designer-header-wrapper">
          <div class="designer-header-title">Visibility Script</div>
        </div>
        <fts-btn
          text
          icon
          class="designer-header-action"
          @click="closeScriptModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </fts-btn>
      </template>

      <template #body>
        <div class="fts-form-element-theme">
          <div class="compact-form-design">
            <fts-text-area
              :formData="scriptModalItem"
              :item="{ fieldName: 'visibilityScript' }"
            />
          </div>
        </div>
      </template>
    </fts-modal>
    <!-- Element Content Modal -->
    <fts-modal
      class="designer-modal"
      headerClass="designer-header"
      contentClass="designer-body"
      footerClass="designer-footer"
      :backdrop="false"
      @close="closeElementContentModal"
      v-if="showElementContentModal"
    >
      <template #header>
        <div class="designer-header-wrapper">
          <div class="designer-header-title">Content Link</div>
        </div>
        <fts-btn
          text
          icon
          class="designer-header-action"
          @click="closeElementContentModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </fts-btn>
      </template>
      <template #body>
        <input
          type="text"
          class="fts-form-control"
          v-model="elementContentLink"
          ref="elementContentLink"
        />
      </template>
    </fts-modal>
    <!-- fts-information-modal -->
    <fts-control-modal
      @ok="confirm($event)"
      :modalNameId="id"
    />
  </div>
</template>
<script>
import Vue from "vue";
import commons from "../commons/commons";
import draggable from "vuedraggable";
import wysiwyg from "vue-wysiwyg";
Vue.use(wysiwyg, {});
import FTSFormDesigner from "../elements/FTSFormDesigner";
import FTSInformationModal from "../design/FTSInformationModal";
import FTSControlModal from "../design/FTSControlModal";

import FTSFormElement from "./FTSFormElement";
import FTSModal from "./FTSModal";
import FTSTextArea from "./FTSTextArea";
import FTSToggleSwitch from "./FTSToggleSwitch";
import FTSToggleButton from "./FTSToggleButton";
import FTSContenteditable from "./FTSContenteditable";
import FTSTippy from "./FTSTippy";
import FTSBtn from "./FTSBtn";
import FTSFileUpload from "./FTSFileUpload";
import FTSReleaseBadge from "./FTSReleaseBadge";

import Swatches from "vue-swatches";
import FTSFormula from "../../components/design/FTSFormula.vue";
import FTSFormulaDisplay from "../../components/design/FTSFormulaDisplay.vue";

let range = null;
let fragment = null;

export default {
  components: {
    draggable,
    "fts-form-designer": FTSFormDesigner,
    "fts-control-modal": FTSControlModal,
    "fts-information-modal": FTSInformationModal,
    "fts-form-element": FTSFormElement,
    "fts-modal": FTSModal,
    "fts-text-area": FTSTextArea,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-toggle-button": FTSToggleButton,
    "fts-contenteditable": FTSContenteditable,
    "fts-file-upload": FTSFileUpload,
    "fts-tippy": FTSTippy,
    "fts-btn": FTSBtn,
    Swatches,
    "fts-release-badge": FTSReleaseBadge,
    "fts-formula": FTSFormula,
    "fts-formula-display": FTSFormulaDisplay,
  },
  data() {
    return {
      id: null,
      textSelection: null,
      formData: {},
      test: { rows: [{ index: 0, items: [] }] },
      dragOptions: commons.dragOptions,
      showScriptModal: false,
      scriptModalItem: null,
      scriptModalType: null,
      showElementContentModal: false,
      elementContentLink: null,
      sectionSelection: false,
      foreColor: null,
      backColor: null,
      // formula
      formulaModal: false,
      formulaModalData: null,
      // editor tools
      editorTools: [
        { name: "bold", icon: ["fas", "bold"] },
        { name: "italic", icon: ["fas", "italic"] },
        { name: "underline", icon: ["fas", "underline"] },
        { name: "strikeThrough", icon: ["fas", "strikethrough"] },
        { name: "justifyLeft", icon: ["fas", "align-left"], beta: true },
        { name: "justifyCenter", icon: ["fas", "align-center"], beta: true },
        { name: "justifyRight", icon: ["fas", "align-right"], beta: true },
        { name: "justifyFull", icon: ["fas", "align-justify"], beta: true },
        { name: "indent", icon: ["fas", "indent"], beta: true },
        { name: "outdent", icon: ["fas", "outdent"], beta: true },
        { name: "insertUnorderedList", icon: ["fas", "list-ul"] },
        { name: "insertOrderedList", icon: ["fas", "list-ol"] },
        { name: "subscript", icon: ["fas", "subscript"] },
        { name: "superscript", icon: ["fas", "superscript"] },
        { name: "forecolor", fieldName: "foreColor" },
        { name: "backcolor", fieldName: "backColor" },
        { name: "h1", text: "H1" },
        { name: "h2", text: "H2" },
        { name: "link", icon: ["fas", "link"], beta: true },
        { name: "unlink", icon: ["fas", "unlink"], beta: true },
        { name: "p", icon: ["fas", "paragraph"] },
        { name: "table", icon: ["fas", "table"], beta: true },
      ],
    };
  },
  props: ["item", "document", "activityId", "form"],
  watch: {
    "item.type": function () {
      if (this.item.type == "tab") {
        if (this.item.tabs == null) {
          Vue.set(this.item, "tabs", [
            {
              id: commons.newUUID(),
              header: "(new tab)",
              design: { rows: [{ index: commons.newUUID(), items: [] }] },
            },
          ]);
        }
      }
    },
  },
  methods: {
    updateElementContent(commandName, showDefaultUI, valArg) {
      this.$refs.editor.focus();
      if (this.textSelection) {
        let sel = document.getSelection();
        sel.removeAllRanges();
        sel.addRange(this.textSelection);
        // } else if (document.selection && this.textSelection.select) {
        //   this.textSelection.select();
        // }
        // exec command
        document.execCommand(commandName, showDefaultUI, valArg);
        // create link
        if (commandName === "createlink") {
          let selection = sel;
          let el = selection.anchorNode.parentElement;
          const hrefVal = el.getAttribute("href");
          el.target = "_blank";
          el.classList.add("fts-link-primary"); /* fts-link-{$theme} */
          // el.innerHTML = "FETIAS custom link" /* (future feature) */
          if (hrefVal)
            el.setAttribute(
              "href",
              hrefVal.toLowerCase().startsWith("http")
                ? hrefVal
                : `//${hrefVal}`
            );
        }
      }
      this.textSelection = null;
    },
    changeColor(type, evt) {
      if (!evt) evt = type === "forecolor" ? "#000" : "transparent";

      this.updateElementContent(type, false, evt);
    },
    onEdit(item, evt) {
      item.content = evt.target.innerHTML;
    },
    onPaste(evt) {
      evt.preventDefault();
      var text = evt.clipboardData.getData("text/plain");
      document.execCommand("insertHTML", false, text);
    },
    getTextSelection() {
      try {
        let sel = window.getSelection();
        let range = sel.getRangeAt(0);
        if (range.endOffset - range.startOffset >= 0)
          this.textSelection = range;
        else if (document.selection && document.selection.createRange)
          this.textSelection = document.selection.createRange();
        else throw "error";
      } catch (err) {
        this.textSelection = null;
      }
    },
    contentEdit(command) {
      this.textSelection = true;

      if (command == "h1" || command == "h2" || command == "p") {
        document.execCommand("formatBlock", false, command);
      } else if (command == "table") {
        var column = parseInt(prompt("Please enter number of column", "2"));
        var row = parseInt(prompt("Please enter number of row", "2"));

        var internal =
          '<table style="border: 1px solid black; border-collapse: collapse;">';
        var i, j;
        for (i = 0; i < row; i++) {
          internal += "<tr>";
          for (j = 0; j < column; j++) {
            internal +=
              '<td style="border: 1px solid black;">type text here...</td>';
          }
          internal += "</tr>";
        }

        internal += "</table>";
        document.execCommand("insertHTML", false, internal);
      } else {
        document.execCommand(command);
      }
      this.textSelection = null;
    },
    // Element Content Modal
    openElementContentModal() {
      this.showElementContentModal = true;
      try {
        const hrefVal =
          this.textSelection.commonAncestorContainer.parentElement.getAttribute(
            "href"
          );
        if (hrefVal)
          this.elementContentLink = hrefVal.startsWith("//")
            ? hrefVal.substring(2)
            : hrefVal;
      } catch (err) {}
    },
    closeElementContentModal() {
      this.showElementContentModal = false;
      // Set Link
      if (this.elementContentLink) {
        this.updateElementContent("createlink", false, this.elementContentLink);
        this.elementContentLink = null;
      }
    },
    getTabs(tabs) {
      if (!tabs.find((x) => x.selected) && tabs && tabs.length) {
        Vue.set(tabs[0], "selected", true);
      }
      return tabs;
    },
    addTab() {
      this.item.tabs.push({
        id: commons.newUUID(),
        header: "(new tab)",
        design: { rows: [] },
        // design: { rows: [{ index: commons.newUUID(), items: [] }] }
      });
    },
    removeTab(tab) {
      let ind = this.item.tabs.indexOf(tab);
      this.item.tabs.splice(ind, 1);
    },
    elementAdded(element) {
      this.$emit("added", element);
    },
    remove() {
      this.$emit("remove", this.item);
    },
    getIcon(typeId) {
      return commons.informationIcon(typeId);
    },
    hideColumn(column) {
      if (column.hide == null || column.hide === false) {
        Vue.set(column, "hide", true);
      }
    },
    showColumn(column) {
      Vue.set(column, "hide", false);
    },
    configure(item) {
      let formData = item;
      this.$modal.show("controlModal" + this.id, { formData });
    },
    updateItem(item, arg) {
      if (
        item[arg] === null ||
        item[arg] === undefined ||
        item[arg] === false
      ) {
        this.$set(item, arg, true);
      } else {
        this.$delete(item, arg);
      }
    },
    openScriptModal(item, type) {
      this.scriptModalType = type;
      this.scriptModalItem = item;
      this.showScriptModal = true;
    },
    closeScriptModal(item) {
      this.showScriptModal = false;
    },
    getScriptElemntModel(type) {
      let item = {
        fieldName: type,
      };
      return item;
    },
    confirm(formData) {
      // Update Table Column(s)
      if (this.item.type === 14 && this.item.fields) {
        let column = this.item.fields.find((x) => x.id === formData.id);
        if (column) {
          column = formData;
          return;
        }
      }
      // Update Information
      this.item = formData;
    },
    // Formula Script
    formulaScript(data) {
      return (
        data.formula ||
        data.requiredFormula ||
        data.readOnlyFormula ||
        data.visibleFormula
      );
    },
    openFormulaModal(data) {
      this.formulaModalData = data;
      this.formulaModal = true;
    },
    closeFormulaModal() {
      this.formulaModal = false;
    },
    getActions(item) {
      // ["required", "readOnly", "assignee", "due", "addable", "visible", "formula"]
      if (item.input == "description" || item.input == "image" || item.input == "empty")
        return ["visible", "formula"];
      else if (item.input == "button")
        return ["readOnly", "visible", "formula"];
      else if (
        item.type == 5 ||
        item.type == 6 ||
        item.type == 30 ||
        item.type == 31
      )
        return ["required", "readOnly", "due", "visible", "formula"];
      else if (item.type == 20 || item.type == 21)
        return ["required", "readOnly", "assignee", "visible", "formula"];
      else if (item.type == 14)
        return ["required", "readOnly", "addable", "visible", "formula"];
      else if (item.input == "section") 
        return ["visible", "formula"];
      else 
        return ["required", "readOnly", "visible", "formula"];
    },
  },
  computed: {
    variables() {
      return [
        {
          name: "Form",
          fieldName: "form",
          type: 35,
          moduleId: this.$store.state.vectorId.toString(),
          activityId: this.activityId,
          form: this.form,
        },
      ];
    },
    beta() {
      return this.$store.state.beta;
    },
    getForeColors() {
      return commons.darkPalette.concat([""]);
    },
    getBackColors() {
      return commons.lightPalette.concat([""]);
    },
    editorBar() {
      return this.textSelection;
    },
    information() {
      return this.$store.state.canvas.information;
    },
    vectorId() {
      return this.$store.state.vectorId;
    },
    requiredCheck: {
      get() {
        return this.item.required || this.item.requiredFormula;
      },
      set(val) {
        if (val) {
          this.$set(this.item, "required", true);
        } else {
          this.$delete(this.item, "requiredFormula");
          this.$delete(this.item, "required");
        }
      },
    },
    readOnlyCheck: {
      get() {
        return this.item.readOnly || this.item.readOnlyFormula;
      },
      set(val) {
        if (val) {
          this.$set(this.item, "readOnly", true);
        } else {
          this.$delete(this.item, "readOnlyFormula");
          this.$delete(this.item, "readOnly");
        }
      },
    },
  },
  beforeDestroy() {
    if (this.item.input == "description") {
      this.item.content = this.$refs.editor.innerHTML;
    }
  },
  mounted() {
    // migration custom form input
    // if (this.item.custom) {
    //   if (this.item.type == 7 || this.item.type == 20) {
    //     if (this.item.searchField && !this.item.connectionField) {
    //       console.warn(`Information "${this.item.displayName}" migrating...`)
    //       // set
    //       this.item.connectionField = this.item.searchField
    //       this.item.connectionFieldId = this.item.searchFieldId

    //       console.info(`Information "${this.item.displayName}" migrated successfully`, {
    //         item: this.item
    //       })
    //     } else {
    //       console.log(`Information "${this.item.displayName}" migrated`)
    //     }
    //   }
    // }
    // end
    this.id = this._uid;

    if (this.item.type == 15) {
      if (this.item.design == null || this.item.design.length == 0) {
        Vue.set(this.item, "design", {
          rows: [{ items: [] }],
        });
      }
    }
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

// Formula Modal
.formula-designer-modal {
  .formula-wrapper {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    .formula-label {
      min-width: 0;
      display: flex;
      align-items: center;
      margin-bottom: 0.375rem;
      font-size: .875rem;
      color: var(--fts-form-label);
      .formula-icon {
        margin-right: 0.575rem;
      }
      .formula-name {
        @include text-overflow-hidden;
        flex: 1;
        font-size: 0.875rem;
      }
    }
  }
}
</style>